import { ComponentGeneralWeekdays } from "@/app/lib/generated/graphql-types";
import { DateTime } from "luxon";
import { MutableGeneralOpeningHour } from "@/app/features/sites/opening-hours/model/types";

const WEEKDAY_ORDER = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
] as const;

export type WeekdayName = typeof WEEKDAY_ORDER[number];

export type OpeningHourInterval = {
    startDay: WeekdayName;
    endDay: WeekdayName;
    startTime: string;
    endTime?: string;
}

export type WeekInterval = {
    start: WeekdayName;
    end: WeekdayName;
    days: WeekdayName[];
};

export function getAggregatedOpeningHoursIntervals(
    generalOpeningHours: MutableGeneralOpeningHour[]
): OpeningHourInterval[] {

    const openingHoursWithIntervals: OpeningHourInterval[] = []

    generalOpeningHours.forEach((entry) => {
        const intervals = getConsecutiveWeekdays(entry.weekdays)
        intervals.forEach((interval) => {
          openingHoursWithIntervals.push({
            startDay: interval.start,
            endDay: interval.end,
            startTime: entry.startTime,
            endTime: entry.endTime,
          })
        });
    });

    return openingHoursWithIntervals;
}

export function getLocalizedWeekdayAbbreviation(weekday: WeekdayName): string {
    const offset = WEEKDAY_ORDER.indexOf(weekday);

    return DateTime.fromJSDate(new Date(2025, 0, 6 + offset)) // 6th Jan 2025 is a Monday
        .toFormat('EEE')
        .replace(/\.$/, '')
}

function getConsecutiveWeekdays(
    weekdays: ComponentGeneralWeekdays
): WeekInterval[] {
    const weekdayFlags = WEEKDAY_ORDER.map((day) => Boolean(weekdays[day]));

    let runs = buildConsecutiveRuns(weekdayFlags);

    if (runs.length >= 2) {
        runs = mergeWrappedRuns(runs);
    }

    return runs.map(mapRunsToWeekdayIntervals);
}


function buildConsecutiveRuns(weekdayFlags: boolean[]): { startIdx: number; endIdx: number }[] {
    const runs: { startIdx: number; endIdx: number }[] = [];
    let currentIndex = 0;

    while (currentIndex < WEEKDAY_ORDER.length) {
        // continue until first day set
        if (!weekdayFlags[currentIndex]) { currentIndex++; continue; }
        const startIdx = currentIndex;

        // Move on until first day not set
        while (currentIndex + 1 < WEEKDAY_ORDER.length && weekdayFlags[currentIndex + 1]) currentIndex++;
        const endIdx = currentIndex;

        runs.push({ startIdx, endIdx });
        currentIndex++;
    }

    return runs;
}

function mergeWrappedRuns(runs: { startIdx: number; endIdx: number }[]) {
    const first = runs[0];
    const last = runs[runs.length - 1];
    const sundayIdx = WEEKDAY_ORDER.length - 1; // 6
    if (last.endIdx === sundayIdx && first.startIdx === 0) {
        const merged = { startIdx: last.startIdx, endIdx: first.endIdx };
        const middle = runs.slice(1, runs.length - 1); // keep any middle runs as-is
        runs.splice(0, runs.length, merged, ...middle);
    }
    return runs;
}

function mapRunsToWeekdayIntervals({ startIdx, endIdx }: { startIdx: number; endIdx: number }): WeekInterval {
    const days: WeekdayName[] = [];
    if (startIdx <= endIdx) {
        for (let k = startIdx; k <= endIdx; k++) days.push(WEEKDAY_ORDER[k]);
    } else {
        // wrapped internally, e.g. Sat..Tue
        for (let k = startIdx; k < WEEKDAY_ORDER.length; k++) days.push(WEEKDAY_ORDER[k]);
        for (let k = 0; k <= endIdx; k++) days.push(WEEKDAY_ORDER[k]);
    }
    return { start: days[0], end: days[days.length - 1], days };
}